"use client"

import { useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Badge } from "@/components/ui/badge"
import { Check, ChevronRight, ChevronDown } from "lucide-react"

interface SchemaProperty {
  name: string
  path: string
  type: string | string[]
  format?: string
  description?: string
  required?: boolean
  enum?: string[]
  minimum?: number
  maximum?: number
  minLength?: number
  maxLength?: number
  pattern?: string
  properties?: Record<string, any>
  items?: any
  nullable?: boolean
}

interface SchemaPropertySelectorProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dataSchema: string
  currentScope?: string
  onSelect: (scope: string) => void
}

function parseSchemaProperties(
  schema: any,
  basePath = "#/properties",
  requiredFields: string[] = [],
  scopeFilter?: string,
): SchemaProperty[] {





  const properties: SchemaProperty[] = []

  // If scopeFilter is provided, navigate to that scope first
  let targetSchema = schema
  if (scopeFilter && scopeFilter !== "") {

    const parts = scopeFilter.split("/").filter((p) => p && p !== "#")


    for (const part of parts) {

      if (part === "properties" || part === "items") {

        continue
      }



      if (targetSchema.properties && targetSchema.properties[part]) {

        targetSchema = targetSchema.properties[part]
      } else if (targetSchema.items && targetSchema.items.properties && targetSchema.items.properties[part]) {

        targetSchema = targetSchema.items.properties[part]
      } else {

        console.log(
          "[v0] Available properties:",
          targetSchema.properties ? Object.keys(targetSchema.properties) : "none",
        )
        // Can't find the scope, return empty
        return properties
      }
    }




    if (targetSchema.type === "array" && targetSchema.items) {

      targetSchema = targetSchema.items

      // Update basePath to include /items
      basePath = basePath.replace(/\/properties$/, "/items/properties")

    }


    if (targetSchema.properties) {

    }
  }

  if (!targetSchema.properties) {

    return properties
  }


  Object.entries(targetSchema.properties).forEach(([name, prop]: [string, any]) => {
    const path = `${basePath}/${name}`

    let type = prop.type
    let nullable = false
    if (Array.isArray(type)) {
      nullable = type.includes("null")
      type = type.filter((t: string) => t !== "null")[0] || type[0]
    }

    const property: SchemaProperty = {
      name,
      path,
      type,
      format: prop.format,
      description: prop.description,
      required: requiredFields.includes(name),
      enum: prop.enum,
      minimum: prop.minimum,
      maximum: prop.maximum,
      minLength: prop.minLength,
      maxLength: prop.maxLength,
      pattern: prop.pattern,
      nullable,
    }

    // Handle nested objects
    if (type === "object" && prop.properties) {
      property.properties = prop.properties
    }

    // Handle arrays with object items
    if (type === "array" && prop.items?.type === "object" && prop.items.properties) {
      property.items = prop.items
    }

    properties.push(property)
  })


  console.log(
    "[v0] Property names:",
    properties.map((p) => p.name),
  )
  return properties
}

function TreeNode({
  property,
  level = 0,
  selectedPath,
  currentPath,
  onSelect,
  expandedNodes,
  onToggleExpand,
}: {
  property: SchemaProperty
  level?: number
  selectedPath: string | null
  currentPath: string | null
  onSelect: (path: string) => void
  expandedNodes: Set<string>
  onToggleExpand: (path: string) => void
}) {
  const isSelected = selectedPath === property.path
  const isCurrent = currentPath === property.path
  const hasChildren = property.properties || property.items
  const isExpanded = expandedNodes.has(property.path)

  const getTypeColor = (type: string) => {
    switch (type) {
      case "string":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-400"
      case "number":
      case "integer":
        return "bg-green-500/10 text-green-700 dark:text-green-400"
      case "boolean":
        return "bg-purple-500/10 text-purple-700 dark:text-purple-400"
      case "array":
        return "bg-orange-500/10 text-orange-700 dark:text-orange-400"
      case "object":
        return "bg-pink-500/10 text-pink-700 dark:text-pink-400"
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-400"
    }
  }

  // Parse nested properties if this is an object
  let nestedProperties: SchemaProperty[] = []
  if (property.properties) {
    nestedProperties = parseSchemaProperties({ properties: property.properties }, `${property.path}/properties`, [])
  } else if (property.items?.properties) {
    nestedProperties = parseSchemaProperties(
      { properties: property.items.properties },
      `${property.path}/items/properties`,
      [],
    )
  }

  return (
    <div>
      <button
        onClick={() => onSelect(property.path)}
        className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
          isSelected ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted/50"
        }`}
        style={{ marginLeft: `${level * 20}px` }}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onToggleExpand(property.path)
                  }}
                  className="hover:bg-muted rounded p-0.5"
                >
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </button>
              )}
              <span className="font-mono font-semibold text-sm">{property.name}</span>
              {isCurrent && (
                <Badge variant="secondary" className="text-xs">
                  Current
                </Badge>
              )}
              {property.required && (
                <Badge variant="destructive" className="text-xs">
                  Required
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge
                className={`text-xs ${getTypeColor(Array.isArray(property.type) ? property.type[0] : property.type)}`}
              >
                {Array.isArray(property.type) ? property.type.join(" | ") : property.type}
              </Badge>
              {property.nullable && (
                <Badge variant="outline" className="text-xs">
                  nullable
                </Badge>
              )}
              {property.format && (
                <Badge variant="outline" className="text-xs">
                  {property.format}
                </Badge>
              )}
              {property.enum && (
                <Badge variant="outline" className="text-xs">
                  enum
                </Badge>
              )}
            </div>

            {property.description && <p className="text-xs text-muted-foreground mb-1">{property.description}</p>}

            <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
              {property.enum && <span>Options: {property.enum.join(", ")}</span>}
              {property.minLength !== undefined && <span>Min length: {property.minLength}</span>}
              {property.maxLength !== undefined && <span>Max length: {property.maxLength}</span>}
              {property.minimum !== undefined && <span>Min: {property.minimum}</span>}
              {property.maximum !== undefined && <span>Max: {property.maximum}</span>}
              {property.pattern && <span className="font-mono">Pattern: {property.pattern}</span>}
            </div>
          </div>

          {isSelected && (
            <div className="flex-shrink-0">
              <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                <Check className="h-3 w-3 text-primary-foreground" />
              </div>
            </div>
          )}
        </div>
      </button>

      {hasChildren && isExpanded && (
        <div className="mt-2 space-y-2">
          {nestedProperties.map((nestedProp) => (
            <TreeNode
              key={nestedProp.path}
              property={nestedProp}
              level={level + 1}
              selectedPath={selectedPath}
              currentPath={currentPath}
              onSelect={onSelect}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export function SchemaPropertySelector({
  open,
  onOpenChange,
  dataSchema,
  currentScope,
  onSelect,
}: SchemaPropertySelectorProps) {





  const [selectedProperty, setSelectedProperty] = useState<string | null>(null)
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set())

  let properties: SchemaProperty[] = []
  let basePath = "#/properties"

  if (currentScope && currentScope !== "") {
    basePath = currentScope + "/properties"

  }

  try {
    const parsed = JSON.parse(dataSchema)



    if (parsed.properties) {

    }

    properties = parseSchemaProperties(parsed, basePath, parsed.required || [], currentScope)

  } catch (error) {
    console.error("[v0] Error parsing data schema:", error)
  }

  const handleSelect = () => {
    if (selectedProperty) {
      const keyName = selectedProperty.split("/").pop() || selectedProperty
      onSelect(keyName)
      onOpenChange(false)
    }
  }

  const handleToggleExpand = (path: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Select Data Property</DialogTitle>
          <DialogDescription>Choose a property from your data schema to bind this control to</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[400px] pr-4">
          <div className="space-y-2">
            {properties.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <p>No properties found in data schema</p>
                <p className="text-xs mt-2">Add properties to your data schema first</p>
              </div>
            ) : (
              properties.map((property) => (
                <TreeNode
                  key={property.path}
                  property={property}
                  selectedPath={selectedProperty}
                  currentPath={currentScope}
                  onSelect={setSelectedProperty}
                  expandedNodes={expandedNodes}
                  onToggleExpand={handleToggleExpand}
                />
              ))
            )}
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSelect} disabled={!selectedProperty}>
            Select Property
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
